/** Guias educativos da área "Aprenda". Conteúdo editorial próprio em PT-BR. */

export interface GuideSection {
  heading: string;
  paragraphs?: string[];
  list?: string[];
}

export interface Guide {
  slug: string;
  title: string;
  category: string;
  description: string; // meta description / card
  minutes: number;
  /** Posição na trilha "Primeiros passos em cripto" (1-based); undefined = fora da trilha */
  trilhaOrder?: number;
  sections: GuideSection[];
  faq?: { question: string; answer: string }[];
  /** Slugs de termos do glossário relacionados */
  glossario?: string[];
}

export const TRILHA_NAME = 'Primeiros passos em cripto';

export const guias: Guide[] = [
  {
    slug: 'o-que-e-bitcoin',
    title: 'O que é Bitcoin?',
    category: 'Comece aqui',
    description:
      'Entenda o que é o Bitcoin, quem o criou, como ele funciona sem bancos e por que existe um limite de 21 milhões de unidades — explicado em português simples.',
    minutes: 6,
    trilhaOrder: 1,
    sections: [
      {
        heading: 'Uma moeda digital sem dono',
        paragraphs: [
          'Bitcoin é uma moeda digital criada em 2009 por uma pessoa (ou grupo) sob o pseudônimo Satoshi Nakamoto. A ideia central é simples de enunciar e revolucionária na prática: permitir que duas pessoas troquem valor pela internet sem depender de um banco, empresa ou governo como intermediário.',
          'Diferente do dinheiro tradicional, ninguém "emite" bitcoins por decisão de um comitê. As regras da rede — quantos bitcoins existem, como as transações são validadas — estão escritas em código aberto que qualquer pessoa pode auditar. Quem garante o cumprimento das regras não é uma autoridade, e sim milhares de computadores espalhados pelo mundo que verificam cada transação.',
        ],
      },
      {
        heading: 'Por que só existem 21 milhões?',
        paragraphs: [
          'O código do Bitcoin limita a emissão total a 21 milhões de unidades. Novos bitcoins entram em circulação como recompensa aos mineradores — os computadores que validam transações — e essa recompensa cai pela metade a cada quatro anos, num evento chamado halving.',
          'Essa escassez programada é o argumento central de quem enxerga o Bitcoin como "ouro digital": um ativo cuja oferta não pode ser inflacionada por decisão política. É também por isso que cada bitcoin é divisível em 100 milhões de partes (os satoshis) — não é preciso comprar um bitcoin inteiro.',
        ],
      },
      {
        heading: 'O que dá valor ao Bitcoin?',
        paragraphs: [
          'O valor vem da combinação de escassez, utilidade e confiança coletiva — como acontece com qualquer moeda, aliás. O real vale porque confiamos que será aceito; o Bitcoin vale porque milhões de pessoas, empresas e fundos confiam na sua rede, aceitam-no como pagamento ou reserva e estão dispostos a pagar por ele.',
          'Isso também explica a volatilidade: sem um banco central para estabilizar o preço, o valor do Bitcoin flutua com a demanda. Quedas de 50% ou mais já aconteceram diversas vezes na sua história — e é por isso que nenhuma decisão de compra deve ser tomada por impulso ou promessa de enriquecimento.',
        ],
      },
      {
        heading: 'Bitcoin no Brasil',
        paragraphs: [
          'O Brasil é um dos países com maior adoção de cripto do mundo. É possível comprar Bitcoin em exchanges nacionais e internacionais com Pix, e investidores também podem se expor via ETFs negociados na B3. Ganhos com venda de criptoativos podem ser tributáveis — as regras da Receita Federal mudam com o tempo, então vale confirmar as vigentes antes de operar.',
        ],
      },
    ],
    faq: [
      {
        question: 'Preciso comprar um Bitcoin inteiro?',
        answer:
          'Não. Cada bitcoin é divisível em 100 milhões de satoshis. É possível começar com qualquer valor — R$ 50, por exemplo, compra uma fração proporcional ao preço do dia.',
      },
      {
        question: 'Bitcoin é legal no Brasil?',
        answer:
          'Sim. Comprar, vender e manter Bitcoin é legal. O marco legal dos ativos virtuais (Lei 14.478/2022) estabeleceu diretrizes para o setor, e ganhos podem estar sujeitos a imposto de renda.',
      },
      {
        question: 'Quem controla o Bitcoin?',
        answer:
          'Ninguém individualmente. As regras estão em código aberto, e mudanças exigem consenso amplo entre desenvolvedores, mineradores e usuários da rede. É essa descentralização que diferencia o Bitcoin do dinheiro tradicional.',
      },
    ],
    glossario: ['bitcoin', 'halving', 'blockchain', 'mineracao'],
  },
  {
    slug: 'o-que-e-blockchain',
    title: 'O que é blockchain?',
    category: 'Comece aqui',
    description:
      'Blockchain é o "livro-razão" público que torna o Bitcoin possível. Entenda como blocos, mineração e consenso funcionam — sem jargão técnico.',
    minutes: 6,
    trilhaOrder: 2,
    sections: [
      {
        heading: 'Um caderno público que ninguém consegue apagar',
        paragraphs: [
          'Imagine um caderno onde todas as transações de uma moeda são anotadas: quem enviou, quem recebeu, quanto e quando. Agora imagine que existem milhares de cópias idênticas desse caderno espalhadas pelo mundo, atualizadas ao mesmo tempo — e que alterar uma página antiga exigiria reescrever todas as cópias simultaneamente. Esse caderno é a blockchain.',
          'O nome vem da estrutura: as transações são agrupadas em blocos, e cada bloco é "acorrentado" ao anterior por uma impressão digital criptográfica (hash). Alterar qualquer informação de um bloco antigo mudaria sua impressão digital e quebraria a corrente inteira — o que denunciaria a fraude imediatamente.',
        ],
      },
      {
        heading: 'Quem escreve no caderno? O consenso',
        paragraphs: [
          'Se não há uma autoridade central, quem decide qual transação é válida? A resposta é o mecanismo de consenso. No Bitcoin, é o proof-of-work: mineradores competem resolvendo um problema matemático, e o vencedor ganha o direito de adicionar o próximo bloco — recebendo bitcoins novos como recompensa. Fraudar a rede exigiria mais poder computacional do que todos os outros participantes juntos, o que é economicamente inviável.',
          'Outras redes, como o Ethereum, usam proof-of-stake: em vez de gastar energia, validadores "travam" moedas como garantia (staking) e perdem parte delas se agirem de má-fé.',
        ],
      },
      {
        heading: 'Para que serve além de moedas?',
        paragraphs: [
          'A blockchain resolve um problema geral: como registrar informação de forma pública, ordenada e imutável sem confiar em um intermediário. Por isso ela é usada além das moedas — em contratos inteligentes (programas que executam sozinhos), tokens, registros de propriedade, rastreabilidade de cadeias logísticas e identidade digital.',
          'Vale o ceticismo saudável: nem todo problema precisa de blockchain. Quando há uma entidade central confiável e não há necessidade de registro público, um banco de dados comum costuma ser mais barato e rápido.',
        ],
      },
    ],
    faq: [
      {
        question: 'Blockchain e Bitcoin são a mesma coisa?',
        answer:
          'Não. Blockchain é a tecnologia de registro; Bitcoin foi sua primeira aplicação de sucesso. Existem milhares de blockchains diferentes — Ethereum, Solana e outras — cada uma com regras próprias.',
      },
      {
        question: 'É possível hackear uma blockchain?',
        answer:
          'Alterar o histórico de uma blockchain grande como a do Bitcoin é considerado economicamente inviável. Os ataques que aparecem no noticiário quase sempre exploram pontos periféricos: exchanges, contratos mal escritos, pontes entre redes ou o próprio usuário (phishing) — não a blockchain em si.',
      },
    ],
    glossario: ['blockchain', 'proof-of-work', 'proof-of-stake', 'smart-contract', 'mineracao'],
  },
  {
    slug: 'como-funcionam-carteiras',
    title: 'Como funcionam as carteiras cripto?',
    category: 'Carteiras',
    description:
      'Carteira cripto não guarda moedas — guarda chaves. Entenda chaves públicas e privadas, carteiras quentes e frias, e qual escolher para começar.',
    minutes: 7,
    trilhaOrder: 3,
    sections: [
      {
        heading: 'A carteira não guarda moedas — guarda chaves',
        paragraphs: [
          'O nome engana: uma carteira cripto não armazena moedas. As moedas "vivem" na blockchain, registradas em endereços. O que a carteira guarda são as chaves que provam que aqueles endereços são seus.',
          'Cada carteira tem um par de chaves: a chave pública gera os endereços que você compartilha para receber (como o número de uma conta), e a chave privada é a assinatura secreta que autoriza envios. Quem tem a chave privada controla os fundos — sem exceção, sem "esqueci minha senha", sem suporte para recuperar.',
        ],
      },
      {
        heading: 'Seed phrase: a chave-mestra',
        paragraphs: [
          'Ao criar uma carteira, você recebe uma sequência de 12 ou 24 palavras — a seed phrase (frase de recuperação). Ela é a matriz de todas as suas chaves privadas: com essas palavras, qualquer pessoa, em qualquer dispositivo, reconstrói sua carteira inteira e move seus fundos.',
          'Por isso a regra número um do universo cripto: a seed phrase nunca deve ser digitada em sites, enviada a ninguém nem fotografada. Nenhum suporte legítimo, de nenhuma empresa, jamais pedirá sua seed phrase.',
        ],
      },
      {
        heading: 'Carteira quente ou fria?',
        list: [
          'Carteira quente (hot wallet): aplicativo ou extensão conectada à internet — MetaMask, Trust Wallet e similares. Prática para uso frequente e DeFi, porém mais exposta a malware e phishing.',
          'Carteira fria (cold wallet): dispositivo físico (Ledger, Trezor) que mantém as chaves offline e assina transações sem expô-las. Padrão de segurança para valores relevantes e reservas de longo prazo.',
          'Custódia de terceiros: deixar os ativos na exchange. Conveniente para quem está começando e para valores pequenos, mas significa confiar as chaves a uma empresa — "not your keys, not your coins".',
        ],
        paragraphs: [
          'Não existe resposta única: muitos usuários combinam as três — um pouco na exchange para negociar, uma carteira quente para o dia a dia e uma fria para a reserva.',
        ],
      },
    ],
    faq: [
      {
        question: 'O que acontece se eu perder minha seed phrase?',
        answer:
          'Se você perder a seed phrase E o acesso ao dispositivo onde a carteira está instalada, os fundos ficam inacessíveis para sempre. Não há central de recuperação. Por isso ela deve ser guardada offline, em local seguro — idealmente em mais de uma cópia física.',
      },
      {
        question: 'Posso ter mais de uma carteira?',
        answer:
          'Sim, e é recomendável. Uma prática comum é usar uma carteira "de risco" com poucos fundos para interagir com aplicativos novos e campanhas, mantendo a reserva principal em outra carteira que nunca toca contratos desconhecidos.',
      },
    ],
    glossario: ['carteira-digital', 'seed-phrase', 'exchange'],
  },
  {
    slug: 'carteira-vs-exchange',
    title: 'Carteira própria ou exchange: onde deixar suas criptos?',
    category: 'Carteiras',
    description:
      'Entenda a diferença entre deixar criptomoedas na exchange e ter autocustódia em carteira própria — riscos, vantagens e como decidir.',
    minutes: 6,
    trilhaOrder: 4,
    sections: [
      {
        heading: 'Duas formas de "ter" cripto',
        paragraphs: [
          'Quando você compra Bitcoin numa exchange e o deixa lá, quem controla as chaves privadas é a empresa — você tem um direito contra a exchange, parecido com o saldo num banco. Quando você saca para uma carteira própria, as chaves passam a ser suas: é a autocustódia.',
          'A frase "not your keys, not your coins" (não são suas chaves, não são suas moedas) resume o dilema: na exchange, você depende da solvência e da segurança da empresa; na autocustódia, você depende de si mesmo.',
        ],
      },
      {
        heading: 'Prós e contras de cada caminho',
        list: [
          'Exchange — prós: praticidade, recuperação de senha, liquidez imediata para negociar, integração com Pix.',
          'Exchange — contras: risco de hack ou insolvência da empresa (casos como FTX em 2022 marcaram o setor), bloqueios de saque em crises, dependência de termos de serviço.',
          'Autocustódia — prós: controle total, imunidade a quebras de exchange, acesso a DeFi e aplicativos.',
          'Autocustódia — contras: responsabilidade total pela seed phrase, sem suporte se algo der errado, curva de aprendizado.',
        ],
      },
      {
        heading: 'Uma regra prática',
        paragraphs: [
          'Uma abordagem comum e sensata: valores pequenos e de negociação frequente podem ficar na exchange; reservas relevantes e de longo prazo pertencem à autocustódia — idealmente numa carteira fria. E qualquer que seja o caminho, ative autenticação em dois fatores (2FA) na exchange e teste o processo de saque com um valor pequeno antes de mover quantias maiores.',
        ],
      },
    ],
    faq: [
      {
        question: 'Exchanges brasileiras são seguras?',
        answer:
          'As principais exchanges que atuam no Brasil seguem o marco legal dos ativos virtuais e práticas de segurança do setor, mas nenhuma custódia de terceiros é isenta de risco. Avalie histórico, transparência e regulação — e não concentre tudo num único lugar.',
      },
      {
        question: 'Sacar para carteira própria custa caro?',
        answer:
          'Depende da rede. Saques de Bitcoin e Ethereum pagam taxa de rede (que varia com o congestionamento); redes como Solana ou Layer 2 do Ethereum custam centavos. Muitas exchanges também cobram uma taxa fixa própria de saque.',
      },
    ],
    glossario: ['exchange', 'carteira-digital', 'seed-phrase', 'defi'],
  },
  {
    slug: 'como-proteger-seed-phrase',
    title: 'Como proteger sua seed phrase',
    category: 'Segurança',
    description:
      'A seed phrase é a chave-mestra da sua carteira cripto. Aprenda as práticas corretas de guarda — e os erros que custam tudo.',
    minutes: 5,
    trilhaOrder: 5,
    sections: [
      {
        heading: 'Por que ela é tão crítica',
        paragraphs: [
          'A seed phrase (12 ou 24 palavras) reconstrói sua carteira inteira em qualquer dispositivo. Quem a possui, possui seus fundos — instantaneamente e sem volta. Praticamente todos os roubos de autocustódia acontecem por um destes caminhos: a vítima digitou a frase num site falso, guardou-a em local digital invadido ou foi convencida por um falso suporte a "validá-la".',
        ],
      },
      {
        heading: 'O que fazer',
        list: [
          'Escreva à mão, em papel ou placa de metal, no momento da criação da carteira.',
          'Guarde em local físico seguro — e considere uma segunda cópia em outro local (contra incêndio ou perda).',
          'Considere uma carteira fria para valores relevantes: ela mantém as chaves offline.',
          'Teste a recuperação: antes de depositar valores altos, restaure a carteira a partir da frase para confirmar que a cópia está correta.',
          'Planeje o acesso de emergência: alguém de extrema confiança deve saber que a frase existe e onde está, para o caso de algo acontecer com você.',
        ],
      },
      {
        heading: 'O que nunca fazer',
        list: [
          'Nunca digite a seed phrase em nenhum site, formulário ou aplicativo que não seja a própria carteira durante uma restauração.',
          'Nunca fotografe, salve em nuvem, e-mail, gerenciador de notas ou WhatsApp.',
          'Nunca informe a "suporte técnico" — nenhum suporte legítimo pede a frase, jamais.',
          'Nunca compre carteira física de revendedores não oficiais (podem vir adulteradas com frase pré-gerada).',
          'Nunca use uma frase gerada por outra pessoa ou site.',
        ],
      },
    ],
    faq: [
      {
        question: 'Posso guardar a seed phrase num gerenciador de senhas?',
        answer:
          'É melhor que um arquivo de texto solto, mas ainda é uma cópia digital exposta a invasões e vazamentos. O consenso de segurança é manter a frase apenas em meio físico offline. Para valores pequenos, o risco pode ser aceitável — a decisão é sua, informada.',
      },
      {
        question: 'O que é uma passphrase (25ª palavra)?',
        answer:
          'Algumas carteiras permitem adicionar uma palavra extra secreta à seed phrase, criando uma carteira "escondida". Aumenta a segurança, mas também o risco de perda — se você esquecer a passphrase, nem a seed phrase recupera os fundos.',
      },
    ],
    glossario: ['seed-phrase', 'carteira-digital'],
  },
  {
    slug: 'como-identificar-golpes',
    title: 'Como identificar golpes em cripto',
    category: 'Golpes',
    description:
      'Phishing, falsos suportes, pirâmides e rug pulls: conheça os golpes mais comuns do mundo cripto e os sinais de alerta para não cair neles.',
    minutes: 8,
    trilhaOrder: 6,
    sections: [
      {
        heading: 'A regra de ouro',
        paragraphs: [
          'Se parece bom demais para ser verdade, é golpe. Nenhum investimento legítimo garante lucro, dobra seu dinheiro ou paga "rendimento fixo" alto em cripto. A irreversibilidade das transações torna o setor um alvo preferido de golpistas: uma vez enviado, o dinheiro não volta.',
        ],
      },
      {
        heading: 'Os golpes mais comuns',
        list: [
          'Phishing: sites, e-mails e anúncios que imitam exchanges e carteiras para roubar senhas ou a seed phrase. Confira sempre a URL; prefira favoritos salvos a links de busca ou mensagens.',
          'Falso suporte: perfis que respondem suas dúvidas em redes sociais se passando pela empresa e pedem a seed phrase ou acesso remoto. Suporte legítimo nunca inicia contato por DM.',
          'Pirâmides e "gestores de banca": promessas de rendimento diário garantido, saque travado por "taxas" crescentes. O rendimento vem dos novos entrantes — até o esquema quebrar.',
          'Rug pull: token novo é inflado com marketing, os criadores retiram a liquidez e o preço vai a zero. Sinais: equipe anônima, liquidez não travada, promessas absurdas.',
          'Romance/investimento (pig butchering): relacionamento online que evolui para "dicas de investimento" numa plataforma falsa que simula lucros e nunca deixa sacar.',
          'Aprovações maliciosas: sites que pedem assinatura de transações que dão a um contrato permissão para gastar seus tokens. Leia o que está assinando; revogue aprovações antigas.',
          'Falsos airdrops: páginas que pedem sua seed phrase ou um "depósito de validação" para liberar recompensa. Airdrop legítimo nunca pede nenhum dos dois.',
        ],
      },
      {
        heading: 'Checklist antes de qualquer ação',
        list: [
          'A URL é exatamente a oficial? (golpistas trocam uma letra)',
          'Alguém está me pressionando com urgência ou escassez?',
          'Estão pedindo seed phrase, chave privada ou pagamento antecipado? (reprovação automática)',
          'A promessa envolve lucro garantido? (não existe)',
          'Consigo verificar a informação em canal oficial independente?',
        ],
      },
    ],
    faq: [
      {
        question: 'Caí num golpe. O que fazer?',
        answer:
          'Registre boletim de ocorrência (há delegacias de crimes cibernéticos), reúna provas (endereços, transações, conversas) e notifique a exchange envolvida — se os fundos passarem por uma exchange com KYC, há chance de bloqueio. Desconfie de "empresas de recuperação de cripto": a maioria é um segundo golpe sobre a mesma vítima.',
      },
      {
        question: 'Como sei se um projeto novo é confiável?',
        answer:
          'Verifique: equipe identificada e com histórico, código auditado por empresa conhecida, liquidez travada, comunidade real (não só bots), documentação técnica séria e ausência de promessas de lucro. Mesmo assim, projetos novos são inerentemente arriscados.',
      },
    ],
    glossario: ['seed-phrase', 'defi', 'smart-contract'],
  },
  {
    slug: 'como-comprar-cripto-com-seguranca',
    title: 'Como comprar cripto de forma responsável',
    category: 'Comece aqui',
    description:
      'Passo a passo para a primeira compra de criptomoedas no Brasil: escolha da exchange, Pix, custódia e os princípios de gestão de risco.',
    minutes: 7,
    trilhaOrder: 7,
    sections: [
      {
        heading: 'Antes de comprar: os princípios',
        list: [
          'Invista apenas o que pode perder sem comprometer sua vida financeira. Cripto é um ativo de altíssima volatilidade — quedas de 50% ou mais já ocorreram diversas vezes.',
          'Cripto não substitui reserva de emergência, que deve estar em aplicações estáveis e líquidas.',
          'Desconfie de qualquer pessoa ou serviço prometendo retorno garantido.',
          'Entenda o que está comprando: os guias desta trilha e o glossário existem para isso.',
        ],
      },
      {
        heading: 'O passo a passo da primeira compra',
        list: [
          'Escolha uma exchange estabelecida que aceite Pix (veja nosso comparador de exchanges, em Ferramentas).',
          'Crie a conta e complete o KYC (verificação de identidade com CPF e documento).',
          'Ative a autenticação em dois fatores (2FA) com aplicativo autenticador — evite 2FA por SMS.',
          'Deposite um valor pequeno via Pix para testar o fluxo.',
          'Compre a ordem desejada — para iniciantes, ativos consolidados como Bitcoin e Ethereum concentram a maior parte da liquidez do mercado.',
          'Decida a custódia: para valores pequenos, manter na exchange é aceitável; para reservas, aprenda autocustódia (guias anteriores da trilha).',
          'Registre suas operações para o imposto de renda — apps e planilhas ajudam.',
        ],
      },
      {
        heading: 'Estratégia para quem está começando',
        paragraphs: [
          'Tentar "acertar o fundo" é uma armadilha até para profissionais. A estratégia mais usada por iniciantes é o DCA (aportes fixos recorrentes), que dilui o preço de entrada e remove a emoção da decisão — você pode simular resultados históricos na nossa Calculadora DCA.',
          'E lembre: este guia é educativo, não é recomendação de investimento. A decisão — e a responsabilidade — é sempre sua. Em caso de dúvida, consulte um profissional certificado.',
        ],
      },
    ],
    faq: [
      {
        question: 'Preciso declarar cripto no imposto de renda?',
        answer:
          'Sim: criptoativos acima de determinados valores devem ser declarados como bens, e ganhos em vendas podem ser tributáveis conforme o volume mensal. As regras da Receita Federal mudam periodicamente — confirme as vigentes ou consulte um contador.',
      },
      {
        question: 'Qual o melhor dia ou hora para comprar?',
        answer:
          'Não há resposta confiável — nem para profissionais. O mercado cripto opera 24/7 e é imprevisível no curto prazo. Estratégias de aporte recorrente (DCA) existem justamente para tirar essa decisão da equação.',
      },
      {
        question: 'Devo comprar moedas baratas que "podem explodir"?',
        answer:
          'Preço unitário baixo não significa barato — o que importa é o valor de mercado total. Tokens minúsculos prometendo multiplicação são o habitat preferido de rug pulls. Se decidir explorar, faça-o com valores irrisórios e ciência do risco.',
      },
    ],
    glossario: ['exchange', 'bitcoin', 'ethereum', 'carteira-digital'],
  },
];

export const getGuideBySlug = (slug: string): Guide | undefined =>
  guias.find((guide) => guide.slug === slug);

export const trilhaGuias = guias
  .filter((guide) => guide.trilhaOrder !== undefined)
  .sort((a, b) => (a.trilhaOrder! - b.trilhaOrder!));
